import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Calendar,
  User,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Loader2,
  Ban,
} from "lucide-react";
import { tasksApi } from "../../services/api";
import TaskDetailModal from "./TaskDetailModal";

interface TaskBoardProps {
  matterId: string;
}

interface Task {
  _id: string;
  title: string;
  description: string | null;
  status: "todo" | "doing" | "blocked" | "done";
  priority: "low" | "normal" | "high" | "urgent";
  dueAt: string | null;
  ownerUserId: { _id: string; name: string; email: string } | null;
  assigneeUserIds: { _id: string; name: string }[];
  order: number;
  approvals: any[];
  completedAt: string | null;
}

const columns: { id: string; label: string; Icon: React.FC<any>; color: string }[] = [
  { id: "todo", label: "To Do", Icon: Circle, color: "border-t-slate-400" },
  { id: "doing", label: "Doing", Icon: Loader2, color: "border-t-blue-400" },
  { id: "blocked", label: "Blocked", Icon: Ban, color: "border-t-red-400" },
  { id: "done", label: "Done", Icon: CheckCircle2, color: "border-t-emerald-400" },
];

const priorityColors: Record<string, string> = {
  low: "bg-slate-100 text-slate-600",
  normal: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const TaskBoard: React.FC<TaskBoardProps> = ({ matterId }) => {
  const [grouped, setGrouped] = useState<Record<string, Task[]>>({
    todo: [],
    doing: [],
    blocked: [],
    done: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [quickAddColumn, setQuickAddColumn] = useState<string | null>(null);
  const [quickAddTitle, setQuickAddTitle] = useState("");

  const fetchTasks = useCallback(async () => {
    try {
      const res = await tasksApi.listGrouped(matterId);
      setGrouped({
        todo: res.data.todo || [],
        doing: res.data.doing || [],
        blocked: res.data.blocked || [],
        done: res.data.done || [],
      });
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Optimistic update
    const sourceCol = source.droppableId;
    const destCol = destination.droppableId;

    const newGrouped = { ...grouped };
    const sourceItems = [...(newGrouped[sourceCol] || [])];
    const destItems = sourceCol === destCol ? sourceItems : [...(newGrouped[destCol] || [])];

    const [moved] = sourceItems.splice(source.index, 1);
    if (!moved) return;

    moved.status = destCol as any;
    destItems.splice(destination.index, 0, moved);

    newGrouped[sourceCol] = sourceCol === destCol ? destItems : sourceItems;
    if (sourceCol !== destCol) newGrouped[destCol] = destItems;

    setGrouped(newGrouped);

    // Persist
    try {
      await tasksApi.reorder(draggableId, destination.index, destCol);
    } catch (err) {
      console.error("Failed to reorder:", err);
      fetchTasks(); // Revert on failure
    }
  };

  const handleQuickAdd = async (status: string) => {
    if (!quickAddTitle.trim()) return;
    try {
      await tasksApi.create({
        matterId,
        title: quickAddTitle.trim(),
        status,
      });
      setQuickAddTitle("");
      setQuickAddColumn(null);
      fetchTasks();
    } catch (err) {
      console.error("Failed to create task:", err);
    }
  };

  const handleTaskUpdate = () => {
    fetchTasks();
    setSelectedTask(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {columns.map((col) => {
            const tasks = grouped[col.id] || [];
            const Icon = col.Icon;

            return (
              <div
                key={col.id}
                className={`bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 ${col.color} flex flex-col min-h-[300px]`}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">{col.label}</span>
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                      {tasks.length}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setQuickAddColumn(col.id);
                      setQuickAddTitle("");
                    }}
                    className="p-1 rounded hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                    title={`Add task to ${col.label}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                {/* Quick Add */}
                <AnimatePresence>
                  {quickAddColumn === col.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-3 pt-3"
                    >
                      <input
                        autoFocus
                        type="text"
                        placeholder="Task title..."
                        value={quickAddTitle}
                        onChange={(e) => setQuickAddTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleQuickAdd(col.id);
                          if (e.key === "Escape") setQuickAddColumn(null);
                        }}
                        onBlur={() => {
                          if (!quickAddTitle.trim()) setQuickAddColumn(null);
                        }}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                      />
                      <div className="flex justify-end gap-2 mt-2 mb-1">
                        <button
                          onClick={() => setQuickAddColumn(null)}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleQuickAdd(col.id)}
                          className="text-xs font-medium text-brand hover:text-brand-hover"
                        >
                          Add
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Tasks */}
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 p-3 space-y-2 transition-colors ${
                        snapshot.isDraggingOver ? "bg-brand-light/50" : ""
                      }`}
                    >
                      {tasks.map((task, index) => (
                        <Draggable key={task._id} draggableId={task._id} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              onClick={() => setSelectedTask(task)}
                              className={`bg-white rounded-lg border p-3 cursor-pointer transition-shadow ${
                                dragSnapshot.isDragging
                                  ? "shadow-lg border-brand-light"
                                  : "border-slate-200 hover:shadow-md"
                              }`}
                            >
                              <p className="text-sm font-medium text-slate-800 mb-2">{task.title}</p>

                              <div className="flex flex-wrap items-center gap-1.5">
                                {/* Priority Badge */}
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    priorityColors[task.priority] || priorityColors.normal
                                  }`}
                                >
                                  {task.priority === "urgent" && (
                                    <AlertTriangle className="inline h-3 w-3 mr-0.5 -mt-0.5" />
                                  )}
                                  {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                                </span>

                                {/* Due Date */}
                                {task.dueAt && (
                                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(task.dueAt).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </span>
                                )}
                              </div>

                              {/* Assignee */}
                              {task.ownerUserId && typeof task.ownerUserId === "object" && (
                                <div className="flex items-center gap-1.5 mt-2">
                                  <div
                                    className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                                    style={{ backgroundColor: "var(--color-accent)" }}
                                  >
                                    {task.ownerUserId.name
                                      ?.split(" ")
                                      .slice(0, 2)
                                      .map((w) => w[0]?.toUpperCase())
                                      .join("")}
                                  </div>
                                  <span className="text-xs text-slate-500 truncate">
                                    {task.ownerUserId.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {tasks.length === 0 && !snapshot.isDraggingOver && (
                        <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                          <Icon className="h-6 w-6 mb-1" />
                          <p className="text-xs">No tasks</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
        />
      )}
    </>
  );
};

export default TaskBoard;
